import * as React from "react";
import { CheckmarkRegular } from "@fluentui/react-icons";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Checkbox — visual checkbox indicator.
 *
 * Designed for use inside a clickable container (e.g. a filter list item or
 * column-picker row) where the parent handles the click. By default the
 * checkbox is purely presentational; pass `onChange` to make it interactive.
 *
 * @example
 *   <Checkbox checked={isSelected} />            // visual only
 *   <Checkbox checked={value} onChange={setValue} />  // interactive
 *   <Checkbox checked size="sm" />               // smaller variant
 */
const checkboxVariants = cva(
  "inline-flex items-center justify-center shrink-0 border-[0.15rem] rounded-[0.4rem] transition-all duration-150",
  {
    variants: {
      size: {
        sm: "w-[1.6rem] h-[1.6rem] [&_svg]:w-[1rem] [&_svg]:h-[1rem]",
        md: "w-[1.8rem] h-[1.8rem] [&_svg]:w-[1.2rem] [&_svg]:h-[1.2rem]",
        lg: "w-[2.2rem] h-[2.2rem] [&_svg]:w-[1.6rem] [&_svg]:h-[1.6rem]",
      },
      state: {
        checked: "border-primary bg-primary",
        unchecked: "border-[#d4d4d8] bg-transparent",
      },
    },
    defaultVariants: { size: "md", state: "unchecked" },
  },
);

const Checkbox = React.forwardRef(
  ({ checked = false, size, onChange, className, ...props }, ref) => {
    const interactive = !!onChange;
    const Comp = interactive ? "button" : "span";
    const interactiveProps = interactive
      ? {
          type: "button",
          onClick: (e) => {
            e.stopPropagation();
            onChange(!checked);
          },
          role: "checkbox",
          "aria-checked": checked,
        }
      : {};

    return (
      <Comp
        ref={ref}
        className={cn(
          checkboxVariants({
            size,
            state: checked ? "checked" : "unchecked",
          }),
          interactive &&
            "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
          className,
        )}
        {...interactiveProps}
        {...props}
      >
        {checked && <CheckmarkRegular className="text-white" />}
      </Comp>
    );
  },
);
Checkbox.displayName = "Checkbox";

export { Checkbox, checkboxVariants };
